import { useEffect, useState } from "react";
import { SidebarProvider, SidebarTrigger } from "@/components/ui/sidebar";
import { DashboardSidebar } from "@/components/DashboardSidebar";
import { DashboardHeader } from "@/components/DashboardHeader";
import { DocumentUpload } from "@/components/DocumentUpload";
import { DocumentList } from "@/components/DocumentList";
import { DocumentChecklist } from "@/components/DocumentChecklist";
import { DashboardFooter } from "@/components/DashboardFooter";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";

interface Document {
  id: string;
  name: string;
  size: number;
  type: string;
  path: string;
  created_at: string;
}

const Documents = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const [documents, setDocuments] = useState<Document[]>([]);

  useEffect(() => {
    if (user) {
      fetchDocuments();
    }
  }, [user]);

  const fetchDocuments = async () => {
    try {
      const { data, error } = await supabase
        .from('documents')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setDocuments(data || []);
    } catch (error) {
      console.error('Error fetching documents:', error);
      toast({
        title: "Error",
        description: "Failed to load documents.",
        variant: "destructive",
      });
    }
  };

  return (
    <div className="min-h-screen bg-background dark">
      <SidebarProvider>
        <div className="flex min-h-screen">
          <DashboardSidebar />
          <div className="flex-1 ml-64">
            <DashboardHeader />
            <main className="px-4 py-4 mt-16">
              <div className="container mx-auto">
                <div className="flex justify-between items-center mb-6">
                  <h1 className="text-3xl font-bold text-foreground">Documents</h1>
                  <SidebarTrigger />
                </div>
                
                <div className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <DocumentUpload />
                    <DocumentChecklist />
                  </div>
                  
                  {documents.length > 0 && (
                    <DocumentList 
                      documents={documents}
                      onDocumentUpdate={fetchDocuments}
                    />
                  )}
                </div>
              </div>
            </main>
            <DashboardFooter />
          </div>
        </div>
      </SidebarProvider>
    </div>
  );
};

export default Documents;